// AdminChewHub — single control tower for the Chew Flats Student Portal.
//
// Shows every unit, resident identity state, sticker roster, code inventory
// (labels only — decrypted values NEVER travel here), and the 24h reveal
// audit log so LaSalle can spot suspicious spikes at a glance.
//
// Actions available per row:
//   - Copy portal link (SMS/email out-of-band)
//   - Reset identity (unsets PIN + name so next resident can set up fresh)
//
// Read-only endpoints: /api/chew/hub/units, /api/chew/code-reveals
// Mutating endpoint : /api/chew/units/{id}/reset-identity
import { useEffect, useMemo, useState } from "react";
import {
  Key, Copy, ArrowClockwise, MagnifyingGlass, Warning, Eye, EyeSlash,
  ShieldCheck, Users, WarningCircle, LinkSimple,
} from "@phosphor-icons/react";
import { AdminShell as Layout } from "@/components/Layout";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function StatChip({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-300",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-500",
    amber: "bg-amber-100 text-amber-800 border-amber-500",
    red: "bg-red-100 text-red-800 border-red-500",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-500",
  }[tone] || tones.slate;
  return (
    <div className={`px-3 py-2 border-2 ${tones}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest">{label}</div>
      <div className="font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function CodeBadge({ on, label, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 ${on ? "bg-emerald-100 text-emerald-800 border-emerald-500" : "bg-slate-100 text-slate-400 border-slate-300"}`}>
      {label}{on ? "" : "—"}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminChewHub() {
  const [units, setUnits] = useState([]);
  const [reveals, setReveals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showEmpty, setShowEmpty] = useState(true);

  async function loadAll() {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/chew/hub/units"),
        api.get("/chew/code-reveals?limit=50"),
      ]);
      setUnits(u.data.units || []);
      setReveals(r.data.reveals || []);
    } catch (e) {
      toast.error("Could not load Chew Flats hub");
    } finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, []);

  const stats = useMemo(() => {
    const s = { units: units.length, active: 0, awaiting: 0, reveals_ok: 0, reveals_fail: 0 };
    units.forEach(u => {
      if (u.identity_set) s.active++;
      else s.awaiting++;
      s.reveals_ok += u.reveals_24h_ok || 0;
      s.reveals_fail += u.reveals_24h_fail || 0;
    });
    return s;
  }, [units]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units.filter(u => {
      if (!showEmpty && !u.identity_set) return false;
      if (!q) return true;
      const hay = [
        u.unit, u.student_first_name, u.student_last4,
        ...(u.sticker_roster || []),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [units, query, showEmpty]);

  // Search-first: hide the huge table until the user searches or opts to see all.
  const [showAll, setShowAll] = useState(false);
  const q = query.trim();
  const listVisible = q.length > 0 || showAll;

  async function copyLink(u) {
    // Auto-mint if no token yet — one tap goes from "awaiting mint" to
    // "link copied and ready to text."
    if (!u.student_token) {
      try {
        const r = await api.post(`/chew/units/${u.id}/mint-token`);
        u = { ...u, student_token: r.data.token };
        loadAll();
      } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't mint link")); return; }
    }
    const url = `${window.location.origin}/chew/student/${u.student_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied Unit ${u.unit} link`);
    } catch { toast.error("Copy failed — long-press to select"); }
  }

  async function mintAllMissing() {
    const missing = units.filter(u => !u.student_token).length;
    if (missing === 0) { toast.info("All units already have portal links"); return; }
    if (!window.confirm(`Mint portal links for ${missing} unit${missing === 1 ? "" : "s"}? Existing links won't change.`)) return;
    try {
      const r = await api.post("/chew/units/mint-all-missing");
      toast.success(`Minted ${r.data.minted} link${r.data.minted === 1 ? "" : "s"} · ${r.data.skipped} already had one`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk mint failed")); }
  }

  async function mintToken(u) {
    try {
      const r = await api.post(`/chew/units/${u.id}/mint-token`);
      toast.success(r.data.minted ? `Portal link minted for Unit ${u.unit}` : `Link already existed for Unit ${u.unit}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't mint")); }
  }

  async function resetIdentity(u) {
    if (!window.confirm(`Reset resident identity + PIN for Unit ${u.unit}?\n\nThe portal link stays the same — next resident sets up from scratch.`)) return;
    try {
      await api.post(`/chew/units/${u.id}/reset-identity`);
      toast.success(`Reset Unit ${u.unit}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Reset failed")); }
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="admin-chew-hub">
        {/* HEADER */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-500">Chew Flats · LaSalle Pilot</div>
            <h1 className="text-2xl font-black mt-1">Student Portal Control Tower</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every unit at 5346 Chew Ave in one place — copy portal links, see who's signed up, monitor door-code reveals. Codes themselves are encrypted and NEVER shown here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {stats.units - stats.active - stats.awaiting >= 0 && units.filter(u => !u.student_token).length > 0 && (
              <button onClick={mintAllMissing} data-testid="chub-mint-all"
                className="h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
                <Key weight="fill" size={14}/> Mint {units.filter(u => !u.student_token).length} missing
              </button>
            )}
            <button onClick={loadAll} disabled={loading} data-testid="chub-refresh"
              className="h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest hover:bg-slate-100 disabled:opacity-50 inline-flex items-center gap-2">
              <ArrowClockwise weight="bold" size={14}/> Refresh
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <StatChip label="Units" value={stats.units} tone="slate" testid="chub-stat-units"/>
          <StatChip label="Signed up" value={stats.active} tone="emerald" testid="chub-stat-active"/>
          <StatChip label="Awaiting" value={stats.awaiting} tone="amber" testid="chub-stat-awaiting"/>
          <StatChip label="Reveals (24h)" value={stats.reveals_ok} tone="cyan" testid="chub-stat-reveals"/>
          <StatChip label="Wrong PIN (24h)" value={stats.reveals_fail} tone={stats.reveals_fail > 0 ? "red" : "slate"} testid="chub-stat-fail"/>
        </div>

        {/* TWO-COLUMN: units left, reveals right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* UNITS TABLE */}
          <div className="lg:col-span-2 bg-white border-2 border-[#0f172a]">
            <div className="p-2 border-b-2 border-slate-200 flex items-center gap-2 bg-slate-50">
              <MagnifyingGlass weight="bold" size={16} className="text-slate-500"/>
              <input value={query} onChange={e => setQuery(e.target.value)}
                data-testid="chub-search" placeholder="Search unit, name, or sticker roster…"
                autoFocus
                className="flex-1 h-9 px-2 text-sm bg-transparent focus:outline-none"/>
              {query && (
                <button onClick={() => setQuery("")} data-testid="chub-search-clear"
                  className="text-slate-400 hover:text-slate-800 px-1.5">×</button>
              )}
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
                <input type="checkbox" checked={showEmpty} onChange={e => setShowEmpty(e.target.checked)} data-testid="chub-show-empty"/>
                Awaiting
              </label>
              <button onClick={() => setShowAll(v => !v)} data-testid="chub-toggle-all"
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${showAll ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                {showAll ? "Hide all" : `Show all · ${stats.units}`}
              </button>
            </div>
            {!listVisible ? (
              <div className="p-6 sm:p-10 text-center" data-testid="chub-search-hero">
                <div className="mx-auto w-14 h-14 rounded-full bg-cyan-50 border-2 border-cyan-500 flex items-center justify-center mb-3">
                  <MagnifyingGlass weight="bold" size={22} className="text-cyan-600"/>
                </div>
                <div className="font-black text-slate-900 text-lg">Search {stats.units} units</div>
                <div className="text-[12px] text-slate-500 mt-1">Type a unit number, resident, or code sticker to jump straight to it.</div>
                <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
                  <button onClick={() => setQuery("awaiting")} data-testid="chub-quick-awaiting"
                    className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-black uppercase tracking-widest">
                    Awaiting · {stats.awaiting}
                  </button>
                  <button onClick={() => setShowAll(true)} data-testid="chub-quick-signed"
                    className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-black uppercase tracking-widest">
                    Signed up · {stats.active}
                  </button>
                  {stats.reveals_fail > 0 && (
                    <button onClick={() => setQuery("wrong")} data-testid="chub-quick-fail"
                      className="px-2.5 py-1 rounded-full bg-red-50 text-red-800 border border-red-200 text-[10px] font-black uppercase tracking-widest">
                      Wrong PIN · {stats.reveals_fail}
                    </button>
                  )}
                </div>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-black uppercase tracking-widest text-[10px]">Unit</th>
                    <th className="text-left p-2 font-black uppercase tracking-widest text-[10px]">Resident</th>
                    <th className="text-left p-2 font-black uppercase tracking-widest text-[10px]">On sticker</th>
                    <th className="text-left p-2 font-black uppercase tracking-widest text-[10px]">Codes</th>
                    <th className="text-center p-2 font-black uppercase tracking-widest text-[10px]">24h</th>
                    <th className="text-right p-2 font-black uppercase tracking-widest text-[10px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-400 font-black uppercase text-[10px] tracking-widest">Loading…</td></tr>
                  )}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-400 font-black uppercase text-[10px] tracking-widest">No units match</td></tr>
                  )}
                  {filtered.map(u => (
                    <tr key={u.id} data-testid={`chub-row-${u.unit}`} className="hover:bg-amber-50/50">
                      <td className="p-2 font-black">
                        {u.unit}
                        {u.preview_ok && <span className="ml-1 px-1 py-0.5 text-[8px] bg-amber-500 text-white">PREV</span>}
                      </td>
                      <td className="p-2">
                        {u.identity_set ? (
                          <>
                            <div className="font-black">{u.student_first_name || "—"}</div>
                            <div className="text-slate-400 font-mono text-[10px]">
                              {u.student_last4 ? `Last-4 ${u.student_last4}` : "no last-4"}
                              {u.student_move_in && ` · ${u.student_move_in}`}
                            </div>
                          </>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 border-2 border-amber-500">
                            Awaiting sign-up
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-slate-600">
                        {(u.sticker_roster || []).length > 0
                          ? <span className="text-[11px]">{u.sticker_roster.join(", ")}</span>
                          : <span className="text-slate-300 italic">—</span>}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1 flex-wrap">
                          <CodeBadge on={u.has_door_code} label="DOOR" testid={`chub-code-door-${u.unit}`}/>
                          <CodeBadge on={u.has_key_code} label="KEY" testid={`chub-code-key-${u.unit}`}/>
                          <CodeBadge on={u.has_laundry_code} label="LNDRY" testid={`chub-code-laundry-${u.unit}`}/>
                        </div>
                        {u.unit_code_note && (
                          <div className="mt-1 text-[10px] text-amber-800 italic flex items-start gap-1">
                            <Warning weight="fill" size={11} className="text-amber-500 mt-0.5 shrink-0"/>
                            {u.unit_code_note}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        <div className="text-xs font-black">{u.reveals_24h_ok}</div>
                        {u.reveals_24h_fail > 0 && (
                          <div className="text-[10px] text-red-500 font-black" data-testid={`chub-fail-${u.unit}`}>
                            {u.reveals_24h_fail} wrong
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        {u.student_token ? (
                          <>
                            <button onClick={() => copyLink(u)} data-testid={`chub-copy-${u.unit}`}
                              className="p-1.5 hover:bg-cyan-100 border border-slate-300 mr-1" title="Copy portal link">
                              <Copy weight="bold" size={12}/>
                            </button>
                            <a href={`/chew/student/${u.student_token}${u.preview_ok ? "?preview=1" : ""}`}
                              target="_blank" rel="noreferrer" data-testid={`chub-open-${u.unit}`}
                              className="p-1.5 hover:bg-cyan-100 border border-slate-300 mr-1 inline-block" title="Open portal">
                              <LinkSimple weight="bold" size={12}/>
                            </a>
                          </>
                        ) : (
                          <button onClick={() => mintToken(u)} data-testid={`chub-mint-${u.unit}`}
                            className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[9px] tracking-widest mr-1"
                            title="Mint portal link for this unit">
                            Mint link
                          </button>
                        )}
                        {u.identity_set && (
                          <button onClick={() => resetIdentity(u)} data-testid={`chub-reset-${u.unit}`}
                            className="p-1.5 hover:bg-red-100 border border-slate-300 text-red-600" title="Reset identity + PIN">
                            <ArrowClockwise weight="bold" size={12}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* REVEALS AUDIT LOG */}
          <div className="bg-white border-2 border-[#0f172a]">
            <div className="p-2 border-b-2 border-slate-200 bg-slate-50 flex items-center gap-2">
              <Eye weight="fill" size={14} className="text-red-500"/>
              <div className="font-black uppercase tracking-widest text-[10px]">Reveal audit log</div>
              <div className="text-[10px] text-slate-400 ml-auto">last 50</div>
            </div>
            <div className="max-h-[600px] overflow-y-auto divide-y divide-slate-100" data-testid="chub-reveal-log">
              {loading && <div className="p-6 text-center text-slate-400 font-black uppercase text-[10px] tracking-widest">Loading…</div>}
              {!loading && reveals.length === 0 && (
                <div className="p-6 text-center text-slate-400 text-[10px] font-black uppercase tracking-widest">No reveals yet</div>
              )}
              {reveals.map((r, i) => (
                <div key={i} data-testid={`chub-reveal-${i}`}
                  className={`p-2 text-xs ${r.ok ? "" : "bg-red-50"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-black flex items-center gap-1">
                      {r.ok
                        ? <ShieldCheck weight="fill" size={12} className="text-emerald-500"/>
                        : <WarningCircle weight="fill" size={12} className="text-red-500"/>}
                      Unit {r.unit || "?"}
                      {r.student_first_name && <span className="text-slate-500 font-normal"> · {r.student_first_name}</span>}
                    </div>
                    <div className="text-[10px] text-slate-400" title={r.at}>{timeAgo(r.at)}</div>
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-500 font-mono flex items-center gap-2">
                    <span>{r.ip || "—"}</span>
                    {r.kinds && r.kinds.length > 0 && (
                      <span className="text-slate-400">· {r.kinds.join(" + ")}</span>
                    )}
                    {!r.ok && <span className="text-red-500 font-black uppercase">wrong pin</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
